import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Title, Group, Select, Badge, Text, Stack, Button,
  ScrollArea, Divider, Loader, Center,
  Dialog, Grid, Paper,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { DataTable } from "mantine-datatable";
import { notifications } from "@mantine/notifications";
import { IconCash, IconBuildingBank, IconTrash } from "@tabler/icons-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import HttpClient from "../helpers/HttpClient";
import { ConsumationOrder, ConsumationOrderStatus } from "../types/consumation-order.type";
import dayjs from "dayjs";

type BackendList<T> = { status: number; data: T[] };

const STATUS_LABELS: Record<ConsumationOrderStatus, string> = {
  open: "Offen",
  paid_cash: "Barzahlung",
  booked_to_account: "Auf Konto gebucht",
};

const STATUS_COLORS: Record<ConsumationOrderStatus, string> = {
  open: "orange",
  paid_cash: "green",
  booked_to_account: "blue",
};

const formatCurrency = (v: number) => v.toFixed(2).replace(".", ",") + " €";

const orderTotal = (order: ConsumationOrder) =>
  (order.positions ?? []).reduce((sum, p) => sum + p.quantity * Number(p.unitPrice), 0);

const customerName = (order: ConsumationOrder) =>
  order.person ? `${order.person.firstName} ${order.person.lastName}` : (order.guestName ?? "—");

const RestaurantOrders: React.FC = () => {
  const client = useRef(new HttpClient<ConsumationOrder>("/consumation-order")).current;

  const [orders, setOrders] = useState<ConsumationOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [detailOrder, setDetailOrder] = useState<ConsumationOrder | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ConsumationOrder | null>(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      if (dateFrom) params.dateFrom = dayjs(dateFrom).format("YYYY-MM-DD");
      if (dateTo) params.dateTo = dayjs(dateTo).format("YYYY-MM-DD");
      const res = await client.getAll(params) as unknown as BackendList<ConsumationOrder>;
      setOrders(res.data ?? []);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFrom, dateTo, client]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const turnoverByDay = useMemo(() => {
    const map = new Map<string, number>();
    for (const order of orders) {
      const key = dayjs(order.orderDate).format("DD.MM.");
      map.set(key, (map.get(key) ?? 0) + orderTotal(order));
    }
    return Array.from(map.entries())
      .map(([date, total]) => ({ date, total: parseFloat(total.toFixed(2)) }));
  }, [orders]);

  const articleAmounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const order of orders) {
      for (const pos of order.positions ?? []) {
        const name = pos.article?.name ?? "–";
        map.set(name, (map.get(name) ?? 0) + pos.quantity);
      }
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, amount]) => ({ name, amount }));
  }, [orders]);

  const handlePayCash = async (id: number) => {
    setActionLoading(id);
    try {
      await client.postAction(`${id}/pay-cash`);
      notifications.show({ message: "Als bezahlt markiert", color: "green" });
      await fetchOrders();
    } catch {
      notifications.show({ message: "Fehler", color: "red" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleBookToAccount = async (id: number) => {
    setActionLoading(id);
    try {
      await client.postAction(`${id}/book-to-account`);
      notifications.show({ message: "Auf Konto gebucht", color: "green" });
      await fetchOrders();
    } catch {
      notifications.show({ message: "Fehler", color: "red" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    setActionLoading(deleteTarget.id);
    setDeleteTarget(null);
    try {
      await client.delete(deleteTarget.id);
      notifications.show({ message: "Auftrag gelöscht", color: "green" });
      await fetchOrders();
    } catch {
      notifications.show({ message: "Fehler beim Löschen", color: "red" });
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <>
      <Dialog
        opened={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Auftrag löschen"
        size="sm"
      >
        <Text mb="md">
          Auftrag von <strong>{deleteTarget ? customerName(deleteTarget) : ""}</strong> wirklich löschen?
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
          <Button color="red" onClick={handleDeleteConfirmed}>Löschen</Button>
        </Group>
      </Dialog>

      <Dialog
        opened={!!detailOrder}
        onClose={() => setDetailOrder(null)}
        title={`Auftrag – ${detailOrder ? customerName(detailOrder) : ""}`}
        size="md"
      >
        {detailOrder && (
          <Stack>
            <Text size="sm" c="dimmed">{dayjs(detailOrder.orderDate).format("DD.MM.YYYY HH:mm")}</Text>
            <Badge color={STATUS_COLORS[detailOrder.status]}>{STATUS_LABELS[detailOrder.status]}</Badge>
            <Divider />
            <ScrollArea>
              <Stack gap="xs">
                {(detailOrder.positions ?? []).map((p, i) => (
                  <Group key={i} justify="space-between">
                    <Text size="sm">{p.quantity}x {p.article?.name ?? "–"}</Text>
                    <Text size="sm">{formatCurrency(p.quantity * Number(p.unitPrice))}</Text>
                  </Group>
                ))}
              </Stack>
            </ScrollArea>
            <Divider />
            <Group justify="space-between">
              <Text fw={700}>Gesamt</Text>
              <Text fw={700}>{formatCurrency(orderTotal(detailOrder))}</Text>
            </Group>
            {detailOrder.status === "open" && (
              <Group grow>
                <Button
                  color="green"
                  leftSection={<IconCash size={16} />}
                  loading={actionLoading === detailOrder.id}
                  onClick={() => { handlePayCash(detailOrder.id); setDetailOrder(null); }}
                >
                  Bar bezahlen
                </Button>
                {detailOrder.person && (
                  <Button
                    color="blue"
                    variant="light"
                    leftSection={<IconBuildingBank size={16} />}
                    loading={actionLoading === detailOrder.id}
                    onClick={() => { handleBookToAccount(detailOrder.id); setDetailOrder(null); }}
                  >
                    Auf Konto buchen
                  </Button>
                )}
              </Group>
            )}
          </Stack>
        )}
      </Dialog>

      <Stack>
        <Group justify="space-between" align="flex-end">
          <Title order={3}>Restaurant – Aufträge</Title>
          <Group>
            <DateInput
              placeholder="Datum von"
              value={dateFrom}
                onChange={(v) => setDateFrom(v ? new Date(v) : null)}
              clearable
              valueFormat="DD.MM.YYYY"
              w={150}
            />
            <DateInput
              placeholder="Datum bis"
              value={dateTo}
              onChange={(v) => setDateTo(v ? new Date(v) : null)}
              clearable
              valueFormat="DD.MM.YYYY"
              w={150}
            />
            <Select
              placeholder="Alle Zahlungsarten"
              clearable
              value={statusFilter}
              onChange={setStatusFilter}
              data={[
                { value: "open", label: "Offen" },
                { value: "paid_cash", label: "Barzahlung" },
                { value: "booked_to_account", label: "Auf Konto gebucht" },
              ]}
              w={200}
            />
          </Group>
        </Group>

        {!loading && orders.length > 0 && (
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper withBorder p="md">
                <Text fw={600} mb="sm">Umsatz nach Tag</Text>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={turnoverByDay} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={(v) => v + " €"} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v) => [formatCurrency(Number(v)), "Umsatz"]} />
                    <Bar dataKey="total" name="Umsatz" fill="#228be6" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper withBorder p="md">
                <Text fw={600} mb="sm">Artikel (Menge)</Text>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={articleAmounts}
                    layout="vertical"
                    margin={{ top: 4, right: 8, left: 8, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                    <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v) => [v, "Menge"]} />
                    <Bar dataKey="amount" name="Menge" fill="#40c057" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid.Col>
          </Grid>
        )}

        {loading ? (
          <Center mt="xl"><Loader /></Center>
        ) : (
          <DataTable
            records={orders}
            withTableBorder
            striped
            highlightOnHover
            onRowClick={({ record }) => setDetailOrder(record)}
            columns={[
              {
                accessor: "orderDate",
                title: "Datum",
                render: (o) => dayjs(o.orderDate).format("DD.MM.YYYY HH:mm"),
              },
              {
                accessor: "customer",
                title: "Kunde",
                render: (o) => (
                  <Group gap="xs">
                    <Text size="sm">{customerName(o)}</Text>
                    {o.person && <Badge size="xs" color="blue">Mitglied</Badge>}
                    {!o.person && o.guestName && <Badge size="xs" color="gray">Gast</Badge>}
                  </Group>
                ),
              },
              {
                accessor: "status",
                title: "Status",
                render: (o) => <Badge color={STATUS_COLORS[o.status]}>{STATUS_LABELS[o.status]}</Badge>,
              },
              {
                accessor: "total",
                title: "Gesamt",
                render: (o) => <Text size="sm">{formatCurrency(orderTotal(o))}</Text>,
              },
              {
                accessor: "actions",
                title: "",
                render: (o) => (
                  <Group gap="xs" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                    {o.status === "open" && (
                      <>
                        <Button size="xs" color="green" variant="light" loading={actionLoading === o.id} onClick={() => handlePayCash(o.id)}>Bar</Button>
                        {o.person && (
                          <Button size="xs" color="blue" variant="light" loading={actionLoading === o.id} onClick={() => handleBookToAccount(o.id)}>Konto</Button>
                        )}
                      </>
                    )}
                    <Button size="xs" color="red" variant="subtle" loading={actionLoading === o.id} onClick={() => setDeleteTarget(o)}>
                      <IconTrash size={14} />
                    </Button>
                  </Group>
                ),
              },
            ]}
          />
        )}
      </Stack>
    </>
  );
};

export default RestaurantOrders;
